import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip,
  ReferenceArea, ReferenceLine, ResponsiveContainer, Cell, LabelList,
} from "recharts";

const levelColor = (oms) => {
  if (oms <= 2.4) return "#1A6B5A";
  if (oms <= 4.4) return "#3B82F6";
  if (oms <= 6.9) return "#F59E0B";
  return "#EF4444";
};

const CustomTip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-md px-3 py-2 text-xs max-w-[220px]">
      <div className="font-semibold text-[#1B3A6B]">{p.name}</div>
      <div className="text-[#3E577A] mt-0.5">OMS (maturity): {p.oms} · PES (complexity): {p.pes}</div>
    </div>
  );
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Abbreviate a sub-function / activity name to a maximum of two words.
const abbreviate = (name) => {
  const parts = name.split("—");
  const base = (parts.length > 1 ? parts[1] : parts[0]).replace(/&/g, " ");
  const words = base.split(/\s+/).filter(Boolean);
  return words.slice(0, 2).join(" ");
};

// Spread out bubbles that land on (almost) the same coordinate so each is visible.
const applyJitter = (pts) => {
  const groups = {};
  const keyOf = (p) => `${Math.round(p.x * 2) / 2}|${Math.round(p.y * 2) / 2}`;
  pts.forEach((p) => { (groups[keyOf(p)] = groups[keyOf(p)] || []).push(p); });
  Object.values(groups).forEach((g) => {
    if (g.length < 2) return;
    const R = 0.5;
    g.forEach((p, idx) => {
      const ang = (2 * Math.PI * idx) / g.length;
      const ring = 1 + Math.floor(idx / 8);
      p.x = clamp(p.x + Math.cos(ang) * R * ring, 1, 9);
      p.y = clamp(p.y + Math.sin(ang) * R * ring, 1, 9);
    });
  });
  return pts;
};

/**
 * Transformation Priority Matrix.
 * X-axis = Complexity (PES, 1-9, higher = more complex)
 * Y-axis = Maturity (plotted as 10 - OMS, so higher = more mature)
 */
export const PriorityMatrix = ({ data, height = 460 }) => {
  const points = applyJitter(
    data.map((d) => ({
      x: d.pes,
      y: +(10 - d.oms).toFixed(1),
      name: d.name,
      label: abbreviate(d.name),
      oms: d.oms,
      pes: d.pes,
      critical: d.critical,
    }))
  );

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ScatterChart margin={{ top: 24, right: 28, bottom: 40, left: 8 }}>
        {/* Quadrant fills */}
        <ReferenceArea x1={5} x2={9} y1={5} y2={9} fill="#1A6B5A" fillOpacity={0.06} />
        <ReferenceArea x1={1} x2={5} y1={5} y2={9} fill="#3B82F6" fillOpacity={0.06} />
        <ReferenceArea x1={5} x2={9} y1={1} y2={5} fill="#EF4444" fillOpacity={0.08} />
        <ReferenceArea x1={1} x2={5} y1={1} y2={5} fill="#F59E0B" fillOpacity={0.07} />

        <CartesianGrid stroke="#EEF0F3" />
        <ReferenceLine x={5} stroke="#CBD5E1" strokeDasharray="4 4" />
        <ReferenceLine y={5} stroke="#CBD5E1" strokeDasharray="4 4" />

        <XAxis
          type="number" dataKey="x" domain={[1, 9]} ticks={[1, 3, 5, 7, 9]}
          tick={{ fill: "#3E577A", fontSize: 11 }}
          label={{ value: "Complexity (PES) →", position: "bottom", fill: "#1B3A6B", fontSize: 12, fontWeight: 600 }}
        />
        <YAxis
          type="number" dataKey="y" domain={[1, 9]} ticks={[1, 3, 5, 7, 9]}
          tick={{ fill: "#3E577A", fontSize: 11 }}
          label={{ value: "Maturity →", angle: -90, position: "insideLeft", fill: "#1B3A6B", fontSize: 12, fontWeight: 600 }}
        />
        <ZAxis range={[150, 150]} />
        <Tooltip content={<CustomTip />} cursor={{ strokeDasharray: "3 3" }} />

        <Scatter data={points} isAnimationActive={false}>
          {points.map((p, i) => (
            <Cell key={i} fill={levelColor(p.oms)} stroke="#1B3A6B" strokeWidth={1} />
          ))}
          <LabelList dataKey="label" position="top" offset={8}
            style={{ fontSize: 9, fill: "#1B3A6B", fontWeight: 600 }} />
        </Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  );
};

export const MatrixLegend = () => (
  <div className="grid grid-cols-2 gap-3 mt-4 text-xs">
    {[
      { t: "Critical Risk Zone", d: "High complexity · Low maturity", c: "#EF4444" },
      { t: "Maintain & Advance", d: "High complexity · High maturity", c: "#1A6B5A" },
      { t: "Quick Wins", d: "Low complexity · Low maturity", c: "#F59E0B" },
      { t: "Optimised Foundation", d: "Low complexity · High maturity", c: "#3B82F6" },
    ].map((q) => (
      <div key={q.t} className="flex items-start gap-2">
        <span className="mt-1 h-3 w-3 rounded-sm shrink-0" style={{ background: q.c }} />
        <div>
          <div className="font-semibold text-[#1B3A6B]">{q.t}</div>
          <div className="text-[#3E577A]">{q.d}</div>
        </div>
      </div>
    ))}
  </div>
);

export default PriorityMatrix;
